import React from "react";
import {
  MdAccountBalanceWallet,
  MdTrendingDown,
  MdTrendingUp,
} from "react-icons/md";

const formatAmount = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const AmountValue = ({ value, loading, valueClass = "text-white" }) => {
  if (loading) {
    return <span className="text-lg font-bold opacity-70">…</span>;
  }
  return (
    <span
      className={`text-lg max720:text-base font-extrabold tabular-nums whitespace-nowrap ${valueClass}`}
    >
      <span className="font-semibold opacity-90">Rs&nbsp;</span>
      {formatAmount(value)}
    </span>
  );
};

const STAT_ITEMS = [
  {
    id: "totalIn",
    label: "Cash In",
    desc: "Received from customers & guest sales",
    icon: MdTrendingUp,
    iconClass: "text-emerald-300",
    valueClass: "text-emerald-200",
    cardClass: "bg-white/10",
  },
  {
    id: "totalOut",
    label: "Cash Out",
    desc: "Company payments & expenses",
    icon: MdTrendingDown,
    iconClass: "text-rose-300",
    valueClass: "text-rose-200",
    cardClass: "bg-white/10",
  },
  {
    id: "balance",
    label: "Balance",
    desc: "Current CASH account balance",
    icon: MdAccountBalanceWallet,
    iconClass: "text-amber-200",
    valueClass: "text-amber-100",
    cardClass: "bg-amber-400/15 border border-amber-300/30",
  },
];

const CashPaymentsSummaryHero = ({ summary, loading, accountName = "CASH" }) => {
  const netFlow = Number(summary.totalIn || 0) - Number(summary.totalOut || 0);

  return (
    <div className="shrink-0 rounded-2xl bg-gradient-to-br from-[#155f60] via-[#127072] to-[#0a3d3e] text-white shadow-lg mb-5 overflow-hidden">
      <div className="p-5 max720:p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <MdAccountBalanceWallet size={20} />
              </div>
              <div>
                <h2 className="text-sm font-bold uppercase tracking-wide">
                  Cash payments summary
                </h2>
                <p className="text-xs text-white/70 mt-0.5">
                  {accountName} account · {summary.count || 0} transaction
                  {(summary.count || 0) !== 1 ? "s" : ""} in range
                </p>
              </div>
            </div>
          </div>
          <div className="text-right max720:w-full max720:text-left">
            <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
              Net flow (period)
            </p>
            <p
              className={`text-2xl max720:text-xl font-extrabold ${
                netFlow >= 0 ? "text-emerald-200" : "text-rose-200"
              }`}
            >
              {loading ? "…" : `${netFlow >= 0 ? "+" : ""}${formatAmount(netFlow)}`}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <div className="h-2 rounded-full bg-white/15 overflow-hidden flex">
            {summary.totalIn + summary.totalOut > 0 && (
              <>
                <div
                  className="h-full bg-emerald-400 transition-all duration-500"
                  style={{
                    width: loading
                      ? "0%"
                      : `${Math.round(
                          (summary.totalIn /
                            (summary.totalIn + summary.totalOut)) *
                            100
                        )}%`,
                  }}
                />
                <div
                  className="h-full bg-rose-400 transition-all duration-500"
                  style={{
                    width: loading
                      ? "0%"
                      : `${Math.round(
                          (summary.totalOut /
                            (summary.totalIn + summary.totalOut)) *
                            100
                        )}%`,
                  }}
                />
              </>
            )}
          </div>
          <div className="flex justify-between mt-1.5 text-[10px] text-white/55">
            <span>In {loading ? "…" : formatAmount(summary.totalIn)}</span>
            <span>Out {loading ? "…" : formatAmount(summary.totalOut)}</span>
          </div>
        </div>
      </div>

      <div className="px-5 pb-5 max720:px-4 max720:pb-4 flex flex-col gap-2">
        {STAT_ITEMS.map(
          ({ id, label, desc, icon: Icon, iconClass, valueClass, cardClass }) => (
            <div
              key={id}
              className={`flex items-center justify-between gap-4 rounded-xl px-4 py-3.5 max720:px-3 max720:py-3 ${cardClass}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Icon size={16} className={`shrink-0 ${iconClass}`} />
                  <span className="text-xs font-bold uppercase tracking-wide text-white/85">
                    {label}
                  </span>
                </div>
                <p className="text-[11px] text-white/55 mt-1 pl-6 max720:pl-0 max720:mt-0.5">
                  {desc}
                </p>
              </div>
              <AmountValue
                value={summary[id]}
                loading={loading}
                valueClass={valueClass}
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default CashPaymentsSummaryHero;
